import { useAppDispatch, useAppSelector } from '@acorn-portal/shared/src/hooks/reduxHook';
import { CancellationDate as StoreCancellationDate } from '@acorn-portal/shared/src/pages/cancellation/types';
import {
	CreateCancellationCommand,
	UpdateCancellationCommand,
} from '@acorn-portal/shared/src/services/cancellation-api';
import {
	amendCancellation,
	proposeCancellation,
	updateCancellationDate,
	updateCancellationRatingCommand,
	updateStepCeiling,
} from '@acorn-portal/shared/src/store/cancellation/cancellation.slice';
import { ROUTE_PATHS } from '@acorn-portal/shared/src/utils/Routes';
import { useNavigate } from 'react-router';
import { CancellationDateFormValues } from '../../utils/get-initial-cancellation-date-form-values';
import { checkCancellationCommandChange } from './check-cancellation-command-change';
import { getCancellationDateFromValues } from './get-cancellation-date-from-values';

/** Custom hook to handle the submission of cancellation rating (both propose and amend)
 * This hook abstracts the logic for preparing data, determining whether to call propose or amend endpoints,
 * and managing the associated Redux state updates.
 */
export const useRatingSubmission = () => {
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const policy = useAppSelector((state) => state.fullPolicy);
	const cancellation = useAppSelector((state) => state.cancellation);

	// Helper function, post-rating, for both propose and amend actions
	const updateStorePostRating = (
		apiCommand: CreateCancellationCommand | UpdateCancellationCommand,
		storeCommand: StoreCancellationDate,
	) => {
		dispatch(updateCancellationRatingCommand(apiCommand)); // helps detect future changes and api call skips
		dispatch(updateCancellationDate(storeCommand)); // helps repopulate form with latest values
		dispatch(updateStepCeiling('confirm')); // helps with step validation
	};

	/**
	 * Handles the submission of the cancellation date form - the point of rating.
	 * - Facilitates first time proposals, re-rated amends and when to skip calling the endpoints.
	 * - Updates the redux store following API interactions.
	 * @param values - The form values from the cancellation date form.
	 */
	const handleSubmit = async (dateFormValues: CancellationDateFormValues) => {
		// # Data preparation from user input
		const cancellationReason = cancellation.reasonForCancellation;
		const cancellationDateDescription = dateFormValues.cancellationDateDescription;
		const cancellationDateISO = getCancellationDateFromValues(dateFormValues);
		// Prepare redux store command for the date form
		const storeCommand: StoreCancellationDate = {
			description: cancellationDateDescription,
			date: cancellationDateISO,
		};
		// Prepare /Propose command
		const proposeCommand: CreateCancellationCommand = {
			policyReference: policy.policyNumber,
			cancellationDate: cancellationDateISO,
			cancellationReason,
		};
		const transactionId = cancellation.cancellationRatingResponse?.transactionId; // indicates amend
		// Amend
		if (transactionId) {
			// Look out for changes from the last data submitted
			// First, avoid false positives by checking if the date is still 'NOW' - the BE processes now values
			const unchangedNowDescription =
				cancellationDateDescription === 'NOW' && cancellation.cancellationDate.description === 'NOW';
			// If the description is 'NOW', we omit the date from the change check
			const ignoreInCommandChecks: Array<'date' | 'reason'> = unchangedNowDescription ? ['date'] : [];
			// Check if the cancellation command has changed
			const shouldRate = checkCancellationCommandChange(
				cancellationDateISO,
				cancellation.cancellationRatingCommand?.cancellationDate ?? '',
				cancellationReason,
				cancellation.cancellationRatingCommand?.cancellationReason ?? '',
				ignoreInCommandChecks,
			);
			if (shouldRate) {
				// Prepare /Amend command
				const amendCommand: UpdateCancellationCommand = {
					...proposeCommand,
					transactionId,
				};
				// Call the /Amend endpoint and update redux
				await dispatch(amendCancellation(amendCommand)).unwrap();
				updateStorePostRating(amendCommand, storeCommand);
			}
		} else {
			// Call the /Propose endpoint and update redux - fresh journeys
			await dispatch(proposeCancellation(proposeCommand)).unwrap();
			updateStorePostRating(proposeCommand, storeCommand);
		}

		navigate(ROUTE_PATHS.cancellationConfirmation);
	};

	return {
		handleSubmit,
		updateStorePostRating,
	};
};
